import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  QueueVelocityTracker,
  type VelocitySeverity,
} from '../../lib/trading/queueVelocity';
import {
  recordOrderSample,
  pruneOrderVelocity,
} from '../../components/cards/orderVelocity';
import {
  readVelocityMutes,
  writeVelocityMutes,
} from '../../lib/trading/velocityMutePersistence';

// One-shot queue-velocity spike flash (QueueVelocityTracker). Keep in sync with
// the CSS animation duration on .animate-velocity-spike-warn/-alert.
export const VELOCITY_SPIKE_FLASH_MS = 4_000;

/** The per-order fields the spike machine reads each cadence. OrderCardVM is
 *  structurally assignable, so callers pass their VM array directly. */
export interface VelocitySampleInput {
  key: string;
  queue: number | null;
  filledQty: number;
}

/**
 * Per-user velocity DELIVERY settings (#233). These gate the flash and pick the
 * classification thresholds — they never alter the velocity calculation, the
 * sampling cadence, or the ring history the 1m/5m Δ display reads.
 */
export interface VelocityDeliverySettings {
  warningVisualEnabled: boolean;
  criticalVisualEnabled: boolean;
  warningThreshold: number;
  criticalThreshold: number;
}

/** Today's shipped behavior — both tiers on at 5 / 20 contracts·s⁻¹. */
const DEFAULT_DELIVERY: VelocityDeliverySettings = {
  warningVisualEnabled: true,
  criticalVisualEnabled: true,
  warningThreshold: 5,
  criticalThreshold: 20,
};

export interface VelocitySpikeFlash {
  /** Order keys currently flashing, and at what tier. Keyed by vm.key. */
  velocitySpikeFlash: Map<string, 'warn' | 'alert'>;
  /** Trader-muted order keys (render-truth for the bell UI). */
  velocityMutedIds: Set<string>;
  /** Toggle mute for one order key (both the bell UI and the tracker). */
  toggleVelocityMute: (key: string) => void;
}

// ── Queue-velocity spike alerts (QueueVelocityTracker) ──────────────────
// Contracts/sec at which an order's FIFO queue position is advancing — a
// spike means aggressive flow is clearing the book ahead of you and a fill
// is imminent. The tracker owns detection/thresholds (warn >=5, alert >=20);
// we only feed it, read checkAllSpikes(), and flash the affected card/row.
//
// This hook owns the whole machine — the tracker, the flash map + its one-shot
// clear timers, the mute set, and the per-cadence feed effect (which also feeds
// the module-global orderVelocity ring the 1m/5m Δ display reads separately).
// Callers pass the current order VMs and read back the three render values.
//
// `userId`, when provided, persists the per-order mute set browser-locally so it
// survives a reload/remount while the order is still open (issue #140). Omit it
// (or pass null) for an in-session-only mute.
// `delivery` is optional and defaulted to today's behavior, so pre-#233 call
// sites compile and behave unchanged.
//
// `unconfirmedQueueOrderIds` (#275) is the live set of orders whose CURRENT
// queue sample crossed a delivery threshold but has not yet been confirmed by a
// second agreeing sample. Kalshi's bulk queue response oscillates for an
// unchanged resting order (#240), and a lone ~700k -> ~9k sample reads here as
// a ~138,000 contracts·s⁻¹ advance — an instant `alert` spike off a number the
// queue tier next door is already refusing to ring on.
//
// It is passed as a REF, and it gates the FLASH only. The tracker is still fed
// the raw sample, so the ring history, `getVelocity`, and `estimateFillTime`
// are byte-for-byte what they are today; a confirmed advance still flashes on
// the very next cadence, one sample later. Omit it for the pre-#275 behavior.
export function useVelocitySpikeFlash(
  orderVms: readonly VelocitySampleInput[],
  userId: string | null = null,
  delivery: VelocityDeliverySettings = DEFAULT_DELIVERY,
  unconfirmedQueueOrderIds?: { readonly current: ReadonlySet<string> },
  /** False between authoritative queue reads. Existing history and a
   * legitimately-triggered cue keep aging normally, but no repeated display
   * value is recorded as if it were a new source sample. */
  samplingEnabled: boolean = true,
): VelocitySpikeFlash {
  const {
    warningVisualEnabled,
    criticalVisualEnabled,
    warningThreshold,
    criticalThreshold,
  } = delivery;
  const velocityTrackerRef = useRef<QueueVelocityTracker | null>(null);
  if (velocityTrackerRef.current == null) {
    const tracker = new QueueVelocityTracker();
    // Seed the tracker's mute set from storage so checkAllSpikes() honors a
    // persisted mute from the very first cadence, not just after a re-render.
    for (const key of readVelocityMutes(userId)) tracker.mute(key);
    velocityTrackerRef.current = tracker;
  }
  // Which order keys currently flash, and at what tier. Keyed by vm.key (== the
  // Kalshi orderId; poly-<id> for Polymarket) — the same key recordOrderSample
  // and the queue-critical flash use, so the card/row lookups line up.
  const [velocitySpikeFlash, setVelocitySpikeFlash] = useState<
    Map<string, 'warn' | 'alert'>
  >(() => new Map());
  const velocitySpikeTimersRef = useRef<Map<string, number>>(new Map());
  // Remember queue-unavailable transitions independently from the velocity
  // ring so a retained snapshot schedules one cue cleanup, not one per poll.
  const unavailableQueueKeysRef = useRef<Set<string>>(new Set());
  // Trader-muted orders (render-truth for the bell UI); mirrored into the
  // tracker's own mute set, which checkAllSpikes() honors. Seeded from storage
  // so a persisted mute is already applied on first render (issue #140).
  const [velocityMutedIds, setVelocityMutedIds] = useState<Set<string>>(
    () => readVelocityMutes(userId),
  );
  // Previous cycle's order-key set, to detect departures for tracker.remove().
  const prevOrderKeysRef = useRef<Set<string>>(new Set());

  // Add each spiking order to the flash map at its tier and (re)arm a one-shot
  // clear timer. Severity only escalates within a flash window: a warn already
  // showing is upgraded to alert, never downgraded mid-flash. Mirrors
  // triggerQueueCriticalFlash's Set-plus-timers pattern.
  const triggerVelocitySpikeFlash = useCallback(
    (spikes: Array<{ orderId: string; severity: VelocitySeverity }>) => {
      if (spikes.length === 0) return;
      setVelocitySpikeFlash((prev) => {
        let changed = false;
        const next = new Map(prev);
        for (const { orderId, severity } of spikes) {
          if (severity === 'normal') continue; // checkAllSpikes never returns it
          const cur = next.get(orderId);
          if (cur === severity) continue; // already at this tier — no-op
          // alert outranks warn; don't overwrite an alert with a warn.
          if (cur === 'alert' && severity === 'warn') continue;
          next.set(orderId, severity);
          changed = true;
        }
        // Steady-state spikes (same tier every poll) must not churn a new Map
        // identity — that re-renders the consumer each cycle. Mirror the timer-
        // clear / departed-mute paths and return prev when nothing moved.
        return changed ? next : prev;
      });
      for (const { orderId } of spikes) {
        const existing = velocitySpikeTimersRef.current.get(orderId);
        if (existing != null) window.clearTimeout(existing);
        const timer = window.setTimeout(() => {
          setVelocitySpikeFlash((prev) => {
            if (!prev.has(orderId)) return prev;
            const next = new Map(prev);
            next.delete(orderId);
            return next;
          });
          velocitySpikeTimersRef.current.delete(orderId);
        }, VELOCITY_SPIKE_FLASH_MS);
        velocitySpikeTimersRef.current.set(orderId, timer);
      }
    },
    [],
  );

  useEffect(() => {
    return () => {
      for (const timer of velocitySpikeTimersRef.current.values()) {
        window.clearTimeout(timer);
      }
      velocitySpikeTimersRef.current.clear();
    };
  }, []);

  // Read through a ref in the feed effect below: adding these to its deps would
  // re-run the whole sampling pass on a mere preference toggle.
  const visualEnabledRef = useRef({ warn: warningVisualEnabled, alert: criticalVisualEnabled });
  useEffect(() => {
    visualEnabledRef.current = { warn: warningVisualEnabled, alert: criticalVisualEnabled };
  }, [warningVisualEnabled, criticalVisualEnabled]);

  // Same reason, one level further out: the CALLER's ref is mirrored so the
  // feed effect never has to list it as a dependency. Seeded at first render,
  // so the very first cadence already honours a pending candidate.
  const unconfirmedRef = useRef(unconfirmedQueueOrderIds);
  useEffect(() => {
    unconfirmedRef.current = unconfirmedQueueOrderIds;
  }, [unconfirmedQueueOrderIds]);

  // Classification thresholds are pushed into the tracker, NOT into the flash
  // machinery: existing ring history is reclassified in place, never reseeded.
  useEffect(() => {
    velocityTrackerRef.current?.setThresholds(warningThreshold, criticalThreshold);
  }, [warningThreshold, criticalThreshold]);

  // A disabled tier's in-flight flashes disappear at once. DERIVED during
  // render rather than cleared in an effect: the feed already refuses to add
  // new entries for a disabled tier, so this only has to hide entries that
  // predate the toggle — and deriving avoids a cascading setState-in-effect.
  //
  // The underlying entry and its one-shot timer are deliberately left alone, so
  // re-enabling inside the window resumes the remainder of a genuinely live
  // flash instead of resurrecting it for a full fresh 4s.
  const visibleVelocitySpikeFlash = useMemo(() => {
    if (
      warningVisualEnabled &&
      criticalVisualEnabled &&
      orderVms.every((vm) => vm.queue != null)
    ) {
      return velocitySpikeFlash;
    }
    const unavailableQueueKeys = new Set(
      orderVms.filter((vm) => vm.queue == null).map((vm) => vm.key),
    );
    const next = new Map(velocitySpikeFlash);
    for (const [key, severity] of velocitySpikeFlash) {
      const stillOn = severity === 'warn' ? warningVisualEnabled : criticalVisualEnabled;
      if (!stillOn || unavailableQueueKeys.has(key)) {
        next.delete(key);
      }
    }
    // Preserve map identity when nothing was hidden — consumers re-render on it.
    return next.size === velocitySpikeFlash.size ? velocitySpikeFlash : next;
  }, [velocitySpikeFlash, warningVisualEnabled, criticalVisualEnabled, orderVms]);

  // Per-user isolation relies on the initial-state + tracker seed above: this
  // hook's consumer (PositionTracker) only mounts once authenticated, so userId
  // is present and stable for the hook's lifetime, and a logout unmounts it.
  // A different user therefore always arrives on a fresh mount, which reseeds
  // from their own key — no in-place re-seed effect needed.

  // Mute toggle for a single order — backed by the tracker's mute()/unmute()
  // (which checkAllSpikes() respects) plus React state for the bell UI. The
  // tracker's mute set is kept in lockstep with velocityMutedIds, so isMuted()
  // is the authoritative current state (no stale-closure read of React state).
  const toggleVelocityMute = useCallback((key: string) => {
    const tracker = velocityTrackerRef.current;
    const muting = !(tracker?.isMuted(key) ?? false);
    if (muting) {
      tracker?.mute(key);
      // Stop an in-flight flash at once: drop the map entry AND cancel its
      // one-shot clear timer. Without this, muting mid-flash would leave the
      // row animating until the 4s window expired.
      const timer = velocitySpikeTimersRef.current.get(key);
      if (timer != null) {
        window.clearTimeout(timer);
        velocitySpikeTimersRef.current.delete(key);
      }
      setVelocitySpikeFlash((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
    } else {
      tracker?.unmute(key);
    }
    setVelocityMutedIds((prev) => {
      const next = new Set(prev);
      if (muting) next.add(key);
      else next.delete(key);
      // Persist the new mute truth so it survives reload/remount (issue #140).
      writeVelocityMutes(userId, next);
      return next;
    });
  }, [userId]);

  // Feed the open-order velocity trackers. This effect runs at the 4 Hz
  // marketVersion cadence, but both the orderVelocity ring (1m/5m Δ display)
  // and the QueueVelocityTracker (spike detection) advance history only on the
  // shared MIN_GAP_MS (~2s) boundary: recordOrderSample collapses sub-2s calls
  // in place and reports whether it appended, and we gate tracker.record() on
  // that. Recording every 250ms would collapse the tracker's dt and read
  // trivial book jitter as a spike (its thresholds assume multi-second gaps).
  useEffect(() => {
    if (!samplingEnabled) return;
    const tracker = velocityTrackerRef.current;
    const currentKeys = new Set<string>();
    const currentNumericQueueKeys = new Set<string>();
    for (const vm of orderVms) {
      const appended = recordOrderSample(vm.key, vm.queue, vm.filledQty);
      currentKeys.add(vm.key);
      // Spike tracker needs a numeric queue and a real ~2s append; skip
      // unknown-queue orders and sub-cadence collapses.
      if (vm.queue == null) {
        // A retained/stale queue is exposed as null by useUserKalshiPOS. Drop
        // pre-outage queue history (but keep the trader's mute) so recovery is
        // a silent first sight rather than a synthetic velocity spike.
        if (!unavailableQueueKeysRef.current.has(vm.key)) {
          unavailableQueueKeysRef.current.add(vm.key);
          tracker?.resetHistory(vm.key);
          const timer = velocitySpikeTimersRef.current.get(vm.key);
          if (timer != null) window.clearTimeout(timer);
          // The render derivation above hides the cue immediately. Clear its
          // backing state asynchronously so it cannot reappear on recovery;
          // deferral also avoids a synchronous effect-driven render cascade.
          const clearTimer = window.setTimeout(() => {
            setVelocitySpikeFlash((prev) => {
              if (!prev.has(vm.key)) return prev;
              const next = new Map(prev);
              next.delete(vm.key);
              return next;
            });
            velocitySpikeTimersRef.current.delete(vm.key);
          }, 0);
          velocitySpikeTimersRef.current.set(vm.key, clearTimer);
        }
      } else {
        unavailableQueueKeysRef.current.delete(vm.key);
        currentNumericQueueKeys.add(vm.key);
        if (appended && tracker) tracker.record(vm.key, vm.queue);
      }
    }

    pruneOrderVelocity(currentKeys);

    // Prune the velocity RING for departed orders to bound the tracker — but
    // PRESERVE the MUTE. `departed` is every order that vanished this cadence,
    // which is the ENTIRE list whenever openOrders transiently empties (a Kalshi
    // disconnect sets it to []; accountStore, and any empty WS frame does too).
    // Dropping the mute there would un-mute an order that returns on reconnect
    // without a reload — the exact live-reconnect bug. So we only remove()
    // UNMUTED departed orders (that also drops their mute, but they had none);
    // muted orders keep both mute and ring. A muted order never flashes, so its
    // stale ring is inert, and the mute is already in place when it returns.
    // velocityMutedIds is likewise left untouched — the bell stays correct
    // through the gap. Stale ids for genuinely filled orders are harmless and
    // reset on reload (the same bounded accumulation as the persisted set).
    const departed: string[] = [];
    for (const key of prevOrderKeysRef.current) {
      if (!currentKeys.has(key)) departed.push(key);
    }
    for (const key of departed) {
      unavailableQueueKeysRef.current.delete(key);
      if (!(tracker?.isMuted(key) ?? false)) tracker?.remove(key);
    }
    prevOrderKeysRef.current = currentKeys;

    // Detect + flash. checkAllSpikes() skips muted orders and returns 'normal'
    // for orders with <2 samples, so a freshly-seen order never flashes.
    //
    // The tracker is fed and pruned above REGARDLESS of the delivery settings,
    // so turning a tier back on resumes against real history instead of
    // manufacturing a fresh first-sight silence. Only the FLASH is gated, and
    // only here — per severity tier, independently.
    if (tracker) {
      const { warn, alert } = visualEnabledRef.current;
      // #275: skip orders whose current queue sample is still unconfirmed. The
      // sample stays in the ring — only this cadence's flash is withheld — so a
      // genuine advance flashes on the next cadence once it is confirmed.
      const unconfirmed = unconfirmedRef.current?.current;
      const spikes = tracker
        .checkAllSpikes()
        .filter(({ orderId }) => currentNumericQueueKeys.has(orderId))
        .filter(({ orderId }) => !unconfirmed?.has(orderId))
        .filter(({ severity }) => (severity === 'alert' ? alert : severity === 'warn' && warn))
        .map(({ orderId, severity }) => ({ orderId, severity }));
      triggerVelocitySpikeFlash(spikes);
    }
  }, [orderVms, samplingEnabled, triggerVelocitySpikeFlash]);

  return {
    velocitySpikeFlash: visibleVelocitySpikeFlash,
    velocityMutedIds,
    toggleVelocityMute,
  };
}
